/**
 * Hollert Stories Page
 * Web Component: <stories-page>
 * - Initializes Swiper sliders for each story section
 * - Builds ToC navigation from data-stories-heading attributes
 * - Tracks active section via IntersectionObserver
 */
class StoriesPage extends HTMLElement {
  connectedCallback() {
    /* Wait for DOM to be ready */
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    this.toc = this.querySelector('.stories-toc');
    this.sections = document.querySelectorAll('[data-stories-heading]');

    if (this.sections.length === 0) return;

    this.initSwipers();
    this.buildToc();
    this.observeSections();
    this.observeFooter();
  }

  initSwipers() {
    /* Wait for Swiper to be available (loaded by theme) */
    const tryInit = () => {
      if (typeof Swiper === 'undefined') {
        setTimeout(tryInit, 200);
        return;
      }

      document.querySelectorAll('[data-stories-swiper]').forEach(el => {
        const slideCount = el.querySelectorAll('.swiper-slide').length;
        const sectionId = el.id.replace('swiper-stories-', '');
        const wrap = el.closest('.stories-item__slider-wrap');
        const prevBtn = wrap ? wrap.querySelector('[data-stories-prev]') : null;
        const nextBtn = wrap ? wrap.querySelector('[data-stories-next]') : null;

        const swiper = new Swiper(el, {
          slidesPerView: slideCount > 1 ? 2.3 : 1,
          spaceBetween: 24,
          grabCursor: true,
          breakpoints: {
            0: { slidesPerView: 1, spaceBetween: 16 },
            480: { slidesPerView: 1.1, spaceBetween: 16 },
            768: { slidesPerView: slideCount > 1 ? 1.8 : 1, spaceBetween: 16 },
            1025: { slidesPerView: slideCount > 1 ? 2.3 : 1, spaceBetween: 24 }
          }
        });

        /* Hide prev button initially, show after first slide change */
        if (prevBtn) {
          prevBtn.style.opacity = '0';
          prevBtn.style.pointerEvents = 'none';
          prevBtn.style.transition = 'opacity 0.3s ease';
          prevBtn.addEventListener('click', () => swiper.slidePrev());
        }
        if (nextBtn) {
          nextBtn.style.transition = 'opacity 0.3s ease';
          nextBtn.addEventListener('click', () => swiper.slideNext());
        }

        const updateNavVisibility = () => {
          if (prevBtn) {
            const showPrev = swiper.activeIndex > 0;
            prevBtn.style.opacity = showPrev ? '1' : '0';
            prevBtn.style.pointerEvents = showPrev ? 'auto' : 'none';
          }
          if (nextBtn) {
            const showNext = !swiper.isEnd;
            nextBtn.style.opacity = showNext ? '1' : '0';
            nextBtn.style.pointerEvents = showNext ? 'auto' : 'none';
          }
        };
        updateNavVisibility();
        swiper.on('slideChange', updateNavVisibility);
      });
    };
    tryInit();
  }

  buildToc() {
    if (!this.toc) return;
    this.toc.innerHTML = '';

    this.sections.forEach((section) => {
      const heading = section.getAttribute('data-stories-heading');
      if (!heading) return;

      const link = document.createElement('a');
      link.textContent = heading;
      link.href = '#' + section.id;
      link.setAttribute('data-toc-target', section.id);

      link.addEventListener('click', (e) => {
        e.preventDefault();
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });

        /* Update active state immediately */
        this.setActive(section.id);
      });

      this.toc.appendChild(link);
    });

    /* Set first item as active by default */
    if (this.sections.length > 0) {
      this.setActive(this.sections[0].id);
    }
  }

  observeSections() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setActive(entry.target.id);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '-15% 0px -50% 0px'
      }
    );

    this.sections.forEach((section) => observer.observe(section));
  }

  observeFooter() {
    /* On mobile: ToC is sticky top, no need to hide for footer */
    const isMobile = window.innerWidth <= 768;
    if (isMobile) return;

    /* Desktop: Hide ToC when footer comes into view */
    const footer = document.querySelector('footer') || document.querySelector('.footer') || document.querySelector('[class*="footer-group"]');
    if (!footer || !this.toc) return;

    const footerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.toc.classList.toggle('is-hidden', entry.isIntersecting);
        });
      },
      { threshold: 0.05 }
    );
    footerObserver.observe(footer);
  }

  setActive(sectionId) {
    if (!this.toc) return;

    this.toc.querySelectorAll('a').forEach((a) => {
      a.classList.toggle('is-active', a.getAttribute('data-toc-target') === sectionId);
    });

    /* Scroll active item into view in ToC */
    const activeLink = this.toc.querySelector('a.is-active');
    if (activeLink) {
      activeLink.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }
}

customElements.define('stories-page', StoriesPage);
